/**
 * @file 模型选型结果
 * @author WanZhongYun(wanzhongyun@baidu.com)
 */

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Table, Popover, Modal } from 'antd';
import * as echarts from 'echarts';
import * as ecStat from 'echarts-stat';
import { IHooksParams, IRecommends } from './../../type';
import { ReactComponent as UploadFileSvg } from './../../images/upload_file.svg';
import { useEffect } from 'react';
import ThanksPng from './../../images/smrt.jpg';
import type { ColumnsType } from 'antd/lib/table';

type EChartsOption = echarts.EChartOption;

const ModelResult: React.FC<IHooksParams> = props => {
    const { resultData, setResultStatus } = props;
    const { param, recommends = [], report = {} } = resultData;

    const handleDownloadClick = () => {
        const scale = 2;
        const input = document.querySelector('.paddle-model-wrapper') as HTMLElement;
        html2canvas(input, { scale }).then(canvas => {
            let imgWidth = canvas.width / scale;
            let imgHeight = canvas.height / scale;
            const imgData = canvas.toDataURL('img/jpeg', 1.0);
            const pdf = new jsPDF('p', 'pt', [imgWidth, imgHeight]);
            pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
            pdf.save('模型结果.pdf');
        });
    };

    const confirmResetParam = () => {
        Modal.confirm({
            className: 'model-result-modal-wrapper',
            title: '重置模型参数',
            icon: <span />,
            content: '重置模型参数后，所有上传文件和已生成结果均不保存，请确认是否重置',
            okText: '确认',
            cancelText: '取消',
            closable: true,
            centered: true,
            onOk: () => {
                setResultStatus(false);
                sessionStorage.setItem('result-status', '');
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            }
        });
    };

    const paramsInput = (() => {
        if (!(param && !!Object.keys(param).length)) {
            return [];
        }
        // 获取一下用户本地上传的文件夹名称
        const formData = sessionStorage.getItem('paddle-model-form');
        let fileName = '无';
        if (formData) {
            const tempData = JSON.parse(formData);

            // 处理undefined和空数组的情况
            if (Object.keys(tempData).includes('file_name') && tempData.file_name && !!tempData.file_name.length) {
                fileName = tempData.file_name[0].name;
            }
        }

        // 获取用户自定义场景信息
        const application_scene = sessionStorage.getItem('application_scene');
        let sceneName = '其他';
        if (application_scene) {
            const sceneData = JSON.parse(application_scene);
            sceneName = sceneData.a3;
        }
        return [
            {
                title: '场景选择',
                list: [
                    {
                        title: '应用场景',
                        isRequire: true,
                        result: param.a !== '其他' ? param.a : sceneName
                    },
                    {
                        title: '任务类别',
                        isRequire: true,
                        result: param.b
                    }
                ]
            },
            {
                title: '模型运行要求',
                list: [
                    {
                        title: '预期预测时间',
                        isRequire: true,
                        result: param.c
                    },
                    {
                        title: '部署系统',
                        isRequire: true,
                        result: param.d
                    },
                    {
                        title: '精度和速度诉求',
                        isRequire: false,
                        result: param.f
                    }
                ]
            },
            {
                title: '数据分析',
                list: [
                    {
                        title: '数据上传',
                        isRequire: true,
                        result: fileName,
                        isIcon: true,
                        Icon: fileName !== '无' ? <UploadFileSvg className="file-icon" /> : ''
                    },
                    {
                        title: '数据特点',
                        isRequire: false,
                        result: param.g
                    }
                ]
            }
        ];
    })();

    const columns: ColumnsType<IRecommends> = [
        {
            title: '基础模型选择',
            key: 'h',
            dataIndex: 'h',
            width: 160,
            fixed: 'left'
        },
        {
            title: '模型特点',
            key: 'i_feature',
            dataIndex: 'i_feature',
            width: 230,
            render: (text: string) => {
                return text.length > 13 ? (
                    <Popover content={text} overlayStyle={{ width: '320px' }}>
                        <span
                            style={{
                                cursor: 'pointer',
                                display: 'inline-block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                width: '180px'
                            }}
                        >
                            {text}
                        </span>
                    </Popover>
                ) : (
                    text
                );
            }
        },
        {
            title: '硬件推荐',
            key: 'e',
            dataIndex: 'e',
            width: 120
        },
        {
            title: (
                <div className="input-size-title">
                    实际模型时间
                    <Popover
                        content="该时间数据包括统计了图片前处理和预测，且时间通过TensorRT FP32测试得到。"
                        overlayStyle={{ width: '320px' }}
                    >
                        <span className="input-size-title-tips" />
                    </Popover>
                </div>
            ),
            key: 'cost',
            dataIndex: 'cost',
            width: 160,
            render: (text: number) => text + 'ms',
            sorter: (a, b) => a.cost - b.cost
        },
        {
            title: (
                <div className="input-size-title">
                    输入尺寸
                    <Popover
                        content="分割模型只有输入网络尺寸，检测模型第一项为输原图尺寸，第二项为输入网络尺寸。"
                        overlayStyle={{ width: '320px' }}
                    >
                        <span className="input-size-title-tips" />
                    </Popover>
                </div>
            ),
            key: 'size',
            dataIndex: 'size',
            width: 140,
            render: (text: string) => {
                return text.split(';').map((item, index) => (
                    <>
                        <div key={item + index} style={{ marginBottom: '4px' }}>
                            {item}
                        </div>
                    </>
                ));
            }
        },
        {
            title: (
                <div className="input-size-title">
                    精度
                    <Popover
                        content="该精度数据表示在对应数据特点下测试得到的预测进度。分割精度指标为mIOU、检测精度指标为mAP（0.5）。"
                        overlayStyle={{ width: '320px' }}
                    >
                        <span className="input-size-title-tips" />
                    </Popover>
                </div>
            ),
            key: 'precision',
            dataIndex: 'precision',
            width: 110,
            sorter: (a, b) => a.precision - b.precision
        },
        {
            title: '模型配置文件',
            key: 'i_config',
            dataIndex: 'i_config',
            width: 135,
            render: (text: string) => (
                <a href={text} target="_blank" rel="noreferrer">
                    config.yml
                </a>
            )
        },
        {
            title: '查看Demo示例',
            key: 'k',
            dataIndex: 'k',
            width: 145,
            render: (text: string) => (
                <a href={text} target="_blank" rel="noreferrer">
                    下载地址
                </a>
            )
        }
    ];

    const featrueColumns = [
        {
            title: '数据特点',
            key: 'data_feature',
            dataIndex: 'data_feature',
            width: 230
        },
        {
            title: '样本分布不均衡',
            key: 'dist_no_balance',
            dataIndex: 'dist_no_balance',
            width: 230,
            render: (text: boolean) => (text ? '是' : '否')
        },
        {
            title: '密集型',
            key: 'thicker',
            dataIndex: 'thicker',
            width: 230,
            render: (text: boolean) => (text ? '是' : '否')
        },
        {
            title: '小目标',
            key: 'small_target',
            dataIndex: 'small_target',
            width: 230,
            render: (text: boolean) => (text ? '是' : '否')
        }
    ];

    const featureData = (() => {
        // 对code字段做映射处理
        const code: 'g1' | 'g2' | 'g3' | 'g4' | 'g5' | 'g6' | 'g7' | 'g8' = report.code;
        const dataMap = {
            g1: {
                data_feature: '是/否',
                dist_no_balance: true,
                thicker: true,
                small_target: false
            },
            g2: {
                data_feature: '是/否',
                dist_no_balance: true,
                thicker: false,
                small_target: false
            },
            g3: {
                data_feature: '是/否',
                dist_no_balance: false,
                thicker: true,
                small_target: false
            },
            g4: {
                data_feature: '是/否',
                dist_no_balance: false,
                thicker: false,
                small_target: false
            },
            g5: {
                data_feature: '是/否',
                dist_no_balance: true,
                thicker: true,
                small_target: true
            },
            g6: {
                data_feature: '是/否',
                dist_no_balance: true,
                thicker: false,
                small_target: true
            },
            g7: {
                data_feature: '是/否',
                dist_no_balance: false,
                thicker: true,
                small_target: true
            },
            g8: {
                data_feature: '是/否',
                dist_no_balance: false,
                thicker: false,
                small_target: true
            }
        };
        return [dataMap[code]];
    })();

    const getPieOption: (title: string, data: any) => EChartsOption = (title, data) => {
        return {
            color: [
                '#2047f3',
                '#7991f3',
                '#72c9f3',
                '#66d190',
                '#eee377',
                '#ff974d',
                '#e55a55',
                '#965b65',
                '#21c3d9',
                '#5b6e96'
            ],
            title: {
                text: title,
                left: 'center',
                bottom: '3%',
                textStyle: {
                    fontSize: 14,
                    fontWeight: 'normal'
                }
            },
            tooltip: {
                trigger: 'item'
            },
            legend: {
                orient: 'vertical',
                type: 'scroll',
                left: '5%',
                top: '5%'
            },
            series: [
                {
                    type: 'pie',
                    radius: ['40%', '55%'],
                    avoidLabelOverlap: false,
                    label: {
                        show: false,
                        position: 'center'
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: 16,
                            fontWeight: 'normal'
                        }
                    },
                    labelLine: {
                        show: false
                    },
                    data: data
                }
            ]
        };
    };

    const getScatterOption: (title: string, data: any) => EChartsOption = (title, data) => {
        return {
            color: [
                '#2047f3',
                '#7991f3',
                '#72c9f3',
                '#66d190',
                '#eee377',
                '#ff974d',
                '#e55a55',
                '#965b65',
                '#21c3d9',
                '#5b6e96'
            ],
            textStyle: {
                fontFamily: 'PingFangSC-Regular',
                fontWeight: 'normal'
            },
            tooltip: {
                trigger: 'item'
            },
            title: {
                text: title,
                left: 'center',
                bottom: '3%',
                textStyle: {
                    fontSize: 14,
                    fontWeight: 'normal'
                }
            },
            xAxis: {},
            yAxis: {},
            grid: { top: 35, bottom: 65 },
            series: [
                {
                    symbolSize: 10,
                    data: data,
                    type: 'scatter'
                }
            ]
        };
    };

    const getBarOption: (title: string, data: any) => EChartsOption = (title, data) => {
        return {
            color: [
                '#2047f3',
                '#7991f3',
                '#72c9f3',
                '#66d190',
                '#eee377',
                '#ff974d',
                '#e55a55',
                '#965b65',
                '#21c3d9',
                '#5b6e96'
            ],
            textStyle: {
                fontFamily: 'PingFangSC-Regular',
                fontWeight: 'normal'
            },
            legend: {
                left: '5%'
            },
            title: {
                text: title,
                left: 'center',
                bottom: '3%',
                textStyle: {
                    fontSize: 14,
                    fontWeight: 'normal'
                }
            },
            xAxis: {
                type: 'category',
                scale: true
            },
            yAxis: {
                type: 'value'
            },
            tooltip: {
                trigger: 'item'
            },
            grid: { top: 35, bottom: 65 },
            series: [
                {
                    data: ecStat.histogram(data, 'squareRoot').data,
                    type: 'bar'
                }
            ]
        };
    };

    useEffect(() => {
        if (!!Object.keys(report).length) {
            report.charts.forEach((item: any, index: number) => {
                // do something
                const paintDom = echarts.init(
                    document.getElementById(`paddle-selector-item-${index}`) as HTMLDivElement
                );
                if (item.type === 'pie') {
                    // 饼图
                    paintDom.setOption(getPieOption(item.title, item.data));
                }
                if (item.type === 'distplot') {
                    // 直方图
                    paintDom.setOption(getBarOption(item.title, item.data));
                }
                if (item.type === 'relplot') {
                    // 散点图
                    paintDom.setOption(getScatterOption(item.title, item.data));
                }
            });
        }
    }, [report]);

    return (
        <div className="paddle-model-result">
            <div className="paddle-model-result-title">模型选型结果</div>
            <div className="paddle-model-result-btn">
                <div className="download-result-btn" onClick={handleDownloadClick}>
                    下载结果
                </div>
                <div className="reset-model-params" onClick={confirmResetParam}>
                    重置选型参数
                </div>
            </div>

            <div className="paddle-model-result-input">
                <div className="paddle-model-result-input-title">参数输入</div>
                <div className="paddle-model-result-input-content">
                    {paramsInput.map(item => (
                        <div className="content-wrapper" key={item.title}>
                            <div className="content-title">{item.title}</div>
                            <div className="content-item-wrapper">
                                {item.list.map((ele: any) => (
                                    <div className="content-item" key={ele.title + ele.result}>
                                        {ele.isRequire ? (
                                            <div className="content-item-item">
                                                <span className="content-item-item-required" />
                                                {ele.title}：{ele.isIcon && ele.Icon}
                                                {ele.result}
                                            </div>
                                        ) : (
                                            <div className="content-item-item">{`${ele.title}：${ele.result}`}</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="paddle-model-result-recommand">
                <div className="paddle-model-result-recommand-title">
                    模型选型推荐
                    <a
                        target="_blank"
                        rel="noreferrer"
                        href="https://github.com/PaddlePaddle/PaddleDetection/tree/release/2.4/configs/smrt#%E4%B8%89%E6%8E%A8%E8%8D%90%E6%A8%A1%E5%9E%8B%E4%BD%BF%E7%94%A8%E5%85%A8%E6%B5%81%E7%A8%8B"
                    >
                        检测训练使用文档
                    </a>
                    <a
                        target="_blank"
                        rel="noreferrer"
                        href="https://github.com/PaddlePaddle/PaddleSeg/tree/release/2.5/configs/smrt#%E4%B8%89%E6%8E%A8%E8%8D%90%E6%A8%A1%E5%9E%8B%E4%BD%BF%E7%94%A8%E5%85%A8%E6%B5%81%E7%A8%8B"
                    >
                        分割训练使用文档
                    </a>
                </div>
                <div className="paddle-model-result-recommand-table">
                    <Table
                        dataSource={recommends}
                        columns={columns}
                        pagination={false}
                        bordered
                        rowKey={record => Math.random() + record.h + 'one'}
                        locale={{ triggerDesc: '', triggerAsc: '', cancelSort: '' }}
                    />
                </div>
            </div>

            {!!Object.keys(report).length && (
                <div className="paddle-model-result-analyze">
                    <div className="paddle-model-result-analyze-title">数据分析报告</div>
                    <div className="paddle-model-result-analyze-content">
                        <div className="paddle-model-result-analyze-content-title">结论建议</div>
                        {report.desc && (
                            <div
                                className="paddle-model-result-analyze-content-desc"
                                {...{
                                    dangerouslySetInnerHTML: {
                                        __html: report.desc[0]
                                    }
                                }}
                            />
                        )}
                        {report.desc &&
                            report.desc.slice(1).map((item: string, index: number) => (
                                <div
                                    className="paddle-model-result-analyze-content-desc"
                                    {...{
                                        dangerouslySetInnerHTML: {
                                            __html: item.slice(0, 5) + (index + 1) + '. ' + item.slice(5)
                                        }
                                    }}
                                    key={item + index}
                                />
                            ))}
                    </div>
                    <div className="paddle-model-result-recommand-table">
                        <Table
                            dataSource={featureData}
                            columns={featrueColumns}
                            pagination={false}
                            bordered
                            className="report-table"
                            rowKey={record => Math.random() + 'two'}
                        />
                    </div>
                    {/* 显示数据图 */}
                    <div className="paddle-selector-charts-wrapper">
                        {report.charts.map((item: any, index: number) => (
                            <div
                                id={`paddle-selector-item-${index}`}
                                className="paddle-selector-charts-item"
                                key={item + index}
                            />
                        ))}
                    </div>
                </div>
            )}

            <div className="smrt-thanks">
                <div className="smrt-thanks-group">欢迎您加入模型选型工具用户交流群：</div>
                <img className="smrt-thanks-image" src={ThanksPng} alt="" />
                <div className="smrt-thanks-obj">
                    特别感谢：感谢林漫晖、顾茜、吉祥、粟亮、李勇超、邵绅宸对项目的贡献，感谢宸曜科技对项目测试的大力支持。
                </div>
            </div>
        </div>
    );
};

export default ModelResult;
